/* eslint-disable no-console */
import {Form, FormInstance} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import {detectAutoDetect} from '../../constants/Common';
import {ISelect} from '../../interface/ISelect';
import {CA_DOCUMENT_TYPE_VALUES} from '../../pages/aiDocumentManager/Constants/commonConstants';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {statusEm} from '../../pages/netZero/utils/commonFunctions';
import {convertData} from '../../pages/OCR/constants/commonFunctions';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import {updateReferenceRecordId} from '../../services/modules/UploadAndJobs/uploadDocs';
import useJSONStore from '../../store/StaticJson/jsonStore';
import useUserStore from '../../store/userStore/userStore';
import {hasUserRole, UserRole} from '../../utils/commonFunctions';
import AverageForm from '../Forms/AverageForm';
import BusinessForm from '../Forms/BusinessForm';
import {upstreamKey} from '../Forms/Constants/IP/UpstreamKeyLabel';
import FuelForm from '../Forms/FuelForm';
import NonRenewableForm from '../Forms/NonRenewable';
import RawAverageForm from '../Forms/RawAverageForm';
import RefriForm from '../Forms/RefriForm';
import RenewableForm from '../Forms/RenewableForm';
import UpstreamForm from '../Forms/UpstreamForm';
import VehicleForm from '../Forms/VehicleForm';
import WaterForm from '../Forms/WaterForm';
import Loader from '../Loader/Loader';
import processTimeFrameData from '../MonthlyYearly/Utils/processAllTimeFrame';
import PageContent from '../PageContent/PageContent';
import {makeCheckKeyLabel} from '../Table/Constants/Common';
import {getFromAndTo, getFromAndToInData} from '../Table/Utils/getFromAndTo';

import getOCRConsumptionCostKeys from './Common/ocrFieldConsumptionCostKeys';
import getPrerequisiteMasterData, {
  PerquisiteMasterAPIIds
} from './Common/perquisiteMasterAPIs';

interface ITemplateProps {
  isNewRecord: boolean;
  caForm: FormInstance;
  efdbData?: any[];
  allFacilities?: any[];
  record?: any;
  handleResponse: (res: any) => void;
  onFinishFailed: () => void;
  onValuesChange: (status: boolean) => void;
}
interface FromComponentProps {
  props?: any;
}
const DocumentUploadTemplate: React.FC<ITemplateProps> = ({
  isNewRecord,
  caForm,
  efdbData,
  allFacilities,
  record,
  handleResponse,
  onFinishFailed,
  onValuesChange
}) => {
  const [formData, setFormData] = useState<any>();
  const userDetails = useUserStore.getState().userDetails;
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCountry, setSelectedCountry] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const [suppliers, setSuppliers] = useState<ISelect[]>([]);

  const removeIdForUpdate = (value: any) => {
    const newValue = Object.keys(value).reduce((acc: any, key) => {
      if (
        key !== '_id' &&
        key !== '__v' &&
        key !== 'key' &&
        key !== 'fullAddress'
      ) {
        acc[key] = value[key] === undefined ? null : value[key];
      }

      return acc;
    }, {});

    return newValue;
  };

  const getFormComponent = (type: string) => {
    switch (type) {
      case CA_DOCUMENT_TYPE_VALUES.FOR_MACHINES:
        return <FuelForm />;

      case CA_DOCUMENT_TYPE_VALUES.FOR_VEHICLE:
        return <VehicleForm />;

      case CA_DOCUMENT_TYPE_VALUES.FOR_REFRIGERANT:
        return <RefriForm />;

      case CA_DOCUMENT_TYPE_VALUES.RENEWABLE:
        return <RenewableForm />;

      case CA_DOCUMENT_TYPE_VALUES.NON_RENEWABLE:
        return <NonRenewableForm />;

      case CA_DOCUMENT_TYPE_VALUES.RAW_MATERIAL_AVERAGE:
        return (
          <RawAverageForm
            data={undefined}
            setSubmitDisabled={() => {}}
            isAverage={true}
            rawMaterialFilter=""
            suppliers={[]}
            setFormSelectedYear={() => {}}
            setIsFormLoading={() => {}}
            isFormLoading={false}
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.RAW_MATERIAL_SUPPLIER_SPECIFIC:
        return (
          <RawAverageForm
            data={undefined}
            setSubmitDisabled={() => {}}
            isAverage={false}
            rawMaterialFilter=""
            suppliers={suppliers}
            setFormSelectedYear={() => {}}
            setIsFormLoading={() => {}}
            isFormLoading={false}
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.CAPITAL_GOOD_AVERAGE:
        return (
          <AverageForm
            setSubmitDisabled={() => {}}
            suppliers={[]}
            isAverage={true}
            data={undefined}
            setFormFacility={() => {}}
            setFormSelectedYear={() => {}}
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.CAPITAL_GOOD_SUPPLIER_SPECIFIC:
        return (
          <AverageForm
            setSubmitDisabled={() => {}}
            suppliers={[]}
            isAverage={false}
            data={undefined}
            setFormFacility={() => {}}
            setFormSelectedYear={() => {}}
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.UPSTREAM_TRANSPORT:
        return (
          <UpstreamForm
            setSubmitDisabled={() => {}}
            suppliers={suppliers}
            setFormFacility={() => {}}
            setFormSelectedYear={() => {}}
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.DOWNSTREAM_TRANSPORT:
        return (
          <UpstreamForm
            setSubmitDisabled={() => {}}
            suppliers={suppliers}
            setFormFacility={() => {}}
            setFormSelectedYear={() => {}}
            type="down"
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.BUSINESS_TRAVEL_TICKETS:
        return (
          <BusinessForm
            formSelectedCountry={undefined}
            setFormSelectedCountry={() => {}}
          />
        );

      case CA_DOCUMENT_TYPE_VALUES.WATER_BILLS:
        return <WaterForm />;

      default:
        return null;
    }
  };

  const componentDetail = useMemo(() => {
    return getPrerequisiteMasterData(record);
  }, [record]);

  const getOCRFieldsData = (): any => {
    const paras = getOCRConsumptionCostKeys(record?.bill_type);

    return {
      data: convertData(
        {...(record?.fields ?? []), year: record.year},
        paras.consumption,
        paras.cost
      )
    };
  };

  useEffect(() => {
    Promise.all(componentDetail.PerquisiteMasterAPIs)
      .then(([res1, res2]) => {
        if (componentDetail.PerquisiteMasterAPIIds) {
          componentDetail.PerquisiteMasterAPIIds.forEach((item) => {
            if (item === PerquisiteMasterAPIIds.SUPPLIER) {
              setSuppliers(
                res2.map((item: any) => {
                  return {
                    label: item?.[upstreamKey.SUPPLIER_NAME],
                    value: item._id,
                    data: item?.data
                  };
                })
              );
            }
          });
        }
        const selectedFacilityRecord = allFacilities?.[record.year]?.find(
          (item: any) => item?._id === record.facility
        );

        if (selectedFacilityRecord) {
          setSelectedCountry(selectedFacilityRecord?.country);
          setFacilityId(selectedFacilityRecord._id);
          setSelectedYear(selectedFacilityRecord.year);
        }

        if (res1?.data) {
          caForm.setFieldsValue(res1?.data);
          setFormData(res1?.data);
          onValuesChange(true);
        } else if (componentDetail.billType === 'non_renewable') {
          const fData: any = getOCRFieldsData();

          const customFromData: any = {
            _id: '',
            facility: record?.facility,
            timeFrameData:
              componentDetail.billType === 'non_renewable'
                ? fData?.data?.timeFrameData
                : [],
            year: fData?.data?.year,
            electricityBoard: undefined
          };

          const eletricityBoardName = fData?.data?.electricity_board
            ?.toString()
            .replace(/\n/g, ' ');

          if (
            eletricityBoardName ===
            'MAHAVITARAN Maharashtra State Electricity Distribution Co Ltd'
          ) {
            customFromData.electricityBoard =
              'Maharashtra State Electricity Distribution Company Limited';
          } else {
            const electricityBoard = useJSONStore.getState().electricBoard;
            const isElectricityBoardExist = electricityBoard.find((item) => {
              return item?.value === eletricityBoardName;
            });

            if (isElectricityBoardExist) {
              customFromData.electricityBoard = eletricityBoardName;
            }
          }

          setFormData(customFromData);
          caForm.resetFields();
          caForm.setFieldsValue(customFromData);
        }

        isNewRecord && onValuesChange(false);
      })
      .catch(() => {
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => {
      // Call onValuesChange when component unmounts
      onValuesChange(true);
    };
  }, []);

  const onSubmit = (value: any) => {
    Object.keys(value).forEach((key) => {
      if (value[key] === undefined) {
        value[key] = null; // Replace with a suitable placeholder value
      }
    });

    let ftData;

    const idKey = `${record.facilityId}/${record.year}`;
    const type = componentDetail.type;

    if (value.year && value.facility) {
      ftData = getFromAndToInData(value.year, value.facility);
    } else {
      ftData = getFromAndTo(idKey);
    }

    const {year, fromDate, toDate, facilityId} = ftData;

    const mainData = processTimeFrameData(
      formData?._id ? removeIdForUpdate(value) : value,
      fromDate,
      toDate
    );

    if (type) {
      mainData[type.key] = type.value;
    }

    if (componentDetail.mainAPI && facilityId && year) {
      componentDetail
        .mainAPI(
          formData?._id ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
          {
            ...mainData,
            ...(hasUserRole(UserRole.Both_role) &&
            !componentDetail?.isConfigurator &&
            componentDetail?.isAction
              ? {
                  [makeCheckKeyLabel.STATUS.key]: statusEm.ACCEPTED,
                  [makeCheckKeyLabel.COMMENT.key]: ''
                }
              : {}),
            facility: facilityId,
            year,
            createdBy: userDetails._id,
            updatedBy: userDetails._id
          },
          formData?._id ? formData?._id : ''
        )
        .then((res: any) => {
          // Call updateReferenceRecordId after mainAPI succeeds
          if (res?.data?._id) {
            updateReferenceRecordId(record._id, res?.data?._id)
              .then(() => {
                handleResponse(res);
              })
              .catch((err) => {
                handleResponse(res);
                console.error('Error updating reference record ID:', err);
              });
          }
        })
        .catch(() => {
          onFinishFailed();
        });
    }
  };

  return isLoading ? (
    <Loader />
  ) : (
    <PageContent>
      <SelectedCountryContext.Provider
        value={{
          selectedCountry,
          efdbData,
          facilityId,
          setSelectedCountry: () => {},
          selectedYear
        }}
      >
        {' '}
        {componentDetail.componentName && (
          <Form
            form={caForm}
            onFinish={onSubmit}
            onFinishFailed={() => {
              onFinishFailed();
            }}
            onValuesChange={() => {
              onValuesChange(false);
            }}
            initialValues={componentDetail.initialValues}
            autoComplete={detectAutoDetect()}
            layout="vertical"
            className="drawerForm"
          >
            <div className="formLayout">
              <Form.Item name={'facility'} hidden={true} />
              <Form.Item name={'year'} hidden={true} />
              {CA_DOCUMENT_TYPE_VALUES.DOWNSTREAM_TRANSPORT ===
                componentDetail.billType && (
                <Form.Item
                  name={'type'}
                  hidden={true}
                  initialValue={'upstream'}
                />
              )}
              {CA_DOCUMENT_TYPE_VALUES.UPSTREAM_TRANSPORT ===
                componentDetail.billType && (
                <Form.Item
                  name={'type'}
                  hidden={true}
                  initialValue={'downstream'}
                />
              )}
              {React.cloneElement(
                getFormComponent(
                  componentDetail.componentName
                ) as React.ReactElement<FromComponentProps>,
                componentDetail.props
              )}
            </div>
          </Form>
        )}
      </SelectedCountryContext.Provider>
    </PageContent>
  );
};

export default DocumentUploadTemplate;
